import { log } from "./logger";
import type { PdtLogParams } from "./types";

type CleanupFn = () => void;

const PAGE_TRANSITION_STORAGE_KEY = "pdt_page_transition_start";
const MAX_FIELD_LENGTH = 1000;
const MAX_STACK_LENGTH = 3000;
const DEDUPE_WINDOW_MS = 5000;

// Module-level: prevent duplicate listener registration
let isInitialized = false;

// Module-level: ensure each error is logged only once even if handler fires twice
const lastLoggedAt = new Map<string, number>();

const truncate = (value: string, maxLen: number) =>
  value.length > maxLen ? `${value.slice(0, maxLen)}…` : value;

const safeStringify = (value: unknown): string => {
  if (typeof value === "string") return value;
  if (
    typeof value === "number" ||
    typeof value === "boolean" ||
    typeof value === "bigint"
  )
    return String(value);
  if (value == null) return "";
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

const toErrorMeta = (err: unknown) => {
  const obj =
    err && typeof err === "object" ? (err as Record<string, unknown>) : null;
  const name =
    typeof obj?.["name"] === "string"
      ? obj["name"]
      : obj
      ? "Error"
      : "UnknownError";
  const message =
    typeof obj?.["message"] === "string"
      ? obj["message"]
      : typeof err === "string"
      ? err
      : safeStringify(err);
  const stack = typeof obj?.["stack"] === "string" ? obj["stack"] : "";

  return {
    name: truncate(name, 120),
    message: truncate(message, MAX_FIELD_LENGTH),
    stack: truncate(stack, MAX_STACK_LENGTH),
  };
};

const tryLogOnce = (key: string): boolean => {
  const now = Date.now();
  const last = lastLoggedAt.get(key) ?? 0;
  if (now - last < DEDUPE_WINDOW_MS) return false;
  lastLoggedAt.set(key, now);
  return true;
};

/**
 * Call this when a redirect/navigation handler fires (before navigating).
 * Stores timestamp and current page name in sessionStorage.
 * On destination page, initGlobalErrorLogging will read it, log the transition time, and remove it.
 *
 * Example (in navigation handler or link onClick):
 *   storePageTransitionStart("landing");
 *   router.push("/listing");
 */
export const storePageTransitionStart = (fromPageName: string): void => {
  if (typeof window === "undefined" || !window.sessionStorage) return;
  try {
    sessionStorage.setItem(
      PAGE_TRANSITION_STORAGE_KEY,
      JSON.stringify({
        timestamp: Date.now(),
        from_page: fromPageName,
      })
    );
  } catch {
    // ignore storage errors
  }
};

const readAndLogPageTransition = (toPageName: string): void => {
  if (typeof window === "undefined" || !window.sessionStorage) return;
  try {
    const raw = sessionStorage.getItem(PAGE_TRANSITION_STORAGE_KEY);
    if (!raw) return;

    const data = JSON.parse(raw) as { timestamp?: number; from_page?: string };
    const timestamp = typeof data?.timestamp === "number" ? data.timestamp : 0;
    const fromPage =
      typeof data?.from_page === "string" ? data.from_page : "unknown";

    sessionStorage.removeItem(PAGE_TRANSITION_STORAGE_KEY);

    if (timestamp <= 0) return;

    const duration = Date.now() - timestamp;
    log({
      page_name: toPageName,
      event_name: "page_transition_time",
      event_id: `${fromPage}->${toPageName}`,
      event_value: duration,
      page_context: {
        funnel_step: fromPage,
        page_name: toPageName,
      },
    });
  } catch {
    sessionStorage.removeItem(PAGE_TRANSITION_STORAGE_KEY);
  }
};

/**
 * Registers global error handlers and logs them via PDT.
 * Logs exactly once per error when it fires; de-dupes duplicate handler invocations.
 *
 * Call this once on page load (e.g. inside a useEffect) and keep the cleanup
 * function to unregister on unmount.
 *
 * Example:
 *   useEffect(() => initGlobalErrorLogging("insurance_landing"), []);
 */
export const initGlobalErrorLogging = (
  page_name: string,
  extra?: Partial<
    Omit<PdtLogParams, "page_name" | "event_name" | "event_value">
  >
): CleanupFn => {
  if (typeof window === "undefined") return () => void 0;

  readAndLogPageTransition(page_name);

  if (isInitialized) return () => void 0;

  isInitialized = true;

  const logSafe = (
    params: Pick<PdtLogParams, "event_name" | "event_value"> & {
      event_id?: unknown;
    }
  ) => {
    try {
      log({
        page_name,
        event_name: params.event_name,
        event_value: params.event_value,
        event_id: params.event_id,
        ...(extra ?? {}),
      });
    } catch {
      // never throw from a global handler
    }
  };

  const onErrorEvent = (event: Event) => {
    try {
      if (!(event instanceof ErrorEvent)) return;

      const meta = toErrorMeta(event.error ?? event.message);
      const payload = {
        message: meta.message,
        name: meta.name,
        source: truncate(event.filename || "", 500),
        lineno: event.lineno ?? 0,
        colno: event.colno ?? 0,
        stack: meta.stack,
      };

      const key = `error:${payload.source}:${payload.lineno}:${payload.colno}:${payload.message}`;
      if (!tryLogOnce(key)) return;

      logSafe({
        event_name: "global_error",
        event_value: truncate(safeStringify(payload), MAX_FIELD_LENGTH),
        event_id: key,
      });
    } catch {
      // swallow
    }
  };

  const onUnhandledRejection = (event: PromiseRejectionEvent) => {
    try {
      const meta = toErrorMeta(event.reason);
      const payload = {
        name: meta.name,
        message: meta.message,
        stack: meta.stack,
      };

      const key = `unhandledrejection:${payload.name}:${payload.message}`;
      if (!tryLogOnce(key)) return;

      logSafe({
        event_name: "global_unhandledrejection",
        event_value: truncate(safeStringify(payload), MAX_FIELD_LENGTH),
        event_id: key,
      });
    } catch {
      // swallow
    }
  };

  window.addEventListener("error", onErrorEvent);
  window.addEventListener("unhandledrejection", onUnhandledRejection);

  return () => {
    isInitialized = false;
    window.removeEventListener("error", onErrorEvent);
    window.removeEventListener("unhandledrejection", onUnhandledRejection);
  };
};
